import {
  deleteContent,
  getContent,
  publishContent,
  saveContent,
  unpublishContent,
} from '@/api/content';
import { EditorState } from '@/types/editor';
import { Image } from '@/types/image';
import { RootState } from '@/types/store';
import { ActionTree, GetterTree, Module, MutationTree } from 'vuex';

type ContentGetter = GetterTree<EditorState, RootState>;

const getDefaultState = (): EditorState => ({
  id: undefined,
  category_id: undefined,
  user_id: undefined,
  author: undefined,
  summary: null,
  featured_image: undefined,
  featured_image_caption: undefined,
  menu_icon: undefined,
  canonical_url: undefined,
  signal_green: undefined,
  signal_amber: undefined,
  signal_red: undefined,
  cta_header: undefined,
  cta_text: undefined,
  cta_link: undefined,
  cta_link_text: undefined,
  created_at: undefined,
  updated_at: undefined,
  published_at: undefined,
  content: '',
  meta: {
    id: '',
    image_id: null,
    image: null,
    latest_commit: null,
    live_commit: null,
    menu_title: null,
    meta_title: null,
    meta_description: null,
    featured_image_description: null,
    noindex: false,
    canonical_url: null,
    open_graph_title: null,
    open_graph_description: null,
    open_graph_image_crop: null,
    slug: '',
    title: '',
    twitter_title: null,
    twitter_description: null,
    twitter_image_crop: null,
  },
});

const state: EditorState = getDefaultState();

const getters: ContentGetter = {
  featured_image: state => state.featured_image,
};

const actions: ActionTree<EditorState, RootState> = {
  async getContent({ commit, dispatch }, { id, commitHash }): Promise<void> {
    dispatch('history/getHistory', id, { root: true });
    if (commitHash === null && localStorage.getItem('editorState') !== null) {
      const json: string = localStorage.getItem('editorState') as string;
      const localState: EditorState = JSON.parse(json);
      if (localState.id === id) {
        commit('setContent', localState);
        localStorage.removeItem('editorState');
      } else {
        commit('setContent', await getContent(id, commitHash));
      }
    } else {
      commit('setContent', await getContent(id, commitHash));
    }
  },
  async deleteContent({ commit, dispatch }, { id, redirect }) {
    try {
      await deleteContent(id, redirect);
      commit('resetEditor');
    } catch (error) {
      dispatch(
        'notifications/error',
        'There was an error trying to delete the content',
        { root: true },
      );
    }
  },
  async publishContent({ commit, dispatch }, id, commitHash = null) {
    try {
      const content = await publishContent(id, commitHash);
      commit('setContent', content);
      dispatch('history/getHistory', id, { root: true });
    } catch (error) {
      dispatch(
        'notifications/error',
        'There was an error trying to publish the content',
        { root: true },
      );
    }
  },
  async unpublishContent({ commit, dispatch }, { id, redirect }) {
    try {
      const content = await unpublishContent(id, redirect);
      commit('setContent', content);
    } catch (error) {
      dispatch(
        'notifications/error',
        'There was an error trying to unpublish the content',
        { root: true },
      );
    }
  },
  resetEditor({ commit }): void {
    commit('resetEditor');
    commit('history/setHistory', [], { root: true });
  },
  async saveContent({ state, commit, dispatch }): Promise<void> {
    localStorage.setItem('editorState', JSON.stringify(state));
    const content = await saveContent(state);
    commit('setContent', content);
    dispatch('history/getHistory', content.id, { root: true });
    localStorage.removeItem('editorState');
  },
};

const mutations: MutationTree<EditorState> = {
  resetEditor(editorState: EditorState): void {
    Object.assign(editorState, getDefaultState());
  },
  setContent(editorState: EditorState, content: EditorState): void {
    editorState = Object.assign(editorState, content);
  },
  setImage(editorState: EditorState, image: Image): void {
    editorState.meta!.image_id = image.id;
    editorState.meta!.image = image;
  },
  setTitle(editorState: EditorState, title: string): void {
    editorState.meta!.title = title;
  },
  setBody(editorState: EditorState, body: string): void {
    editorState.content = body;
  },
  setCategoryId(editorState: EditorState, categoryId: string): void {
    editorState.category_id = categoryId;
  },

  setMetaTitle(editorState: EditorState, title: string): void {
    editorState.meta!.meta_title = title;
  },
  setMetaDescription(editorState: EditorState, description: string): void {
    editorState.meta!.meta_description = description;
  },
  setCanonicalUrl(editorState: EditorState, canonicalUrl: string): void {
    editorState.meta!.canonical_url = canonicalUrl;
  },
  setTwitterTitle(editorState: EditorState, title: string): void {
    editorState.meta!.twitter_title = title;
  },
  setTwitterDescription(editorState: EditorState, description: string): void {
    editorState.meta!.twitter_description = description;
  },
  setOpenGraphTitle(editorState: EditorState, title: string): void {
    editorState.meta!.open_graph_title = title;
  },
  setOpenGraphDescription(editorState: EditorState, description: string): void {
    editorState.meta!.open_graph_description = description;
  },
};

const editor: Module<EditorState, RootState> = {
  state,
  getters,
  actions,
  mutations,
  namespaced: true,
};

export default editor;
